import { MouseEventHandler, useState } from 'react';
import { Icon, Tooltip } from '@scale/scaleui';
import copy from 'copy-to-clipboard';
import { twMerge } from 'tailwind-merge';
import type { IconTooltipProps } from '@scale/scaleui/dist/v1/components/icon/icon';

type CopyAreaProps = {
  stringToCopy: string;
  label?: string;
  className?: string;
  iconClassName?: string;
  tooltip?: string;
  tooltipProps?: IconTooltipProps;
  stopPropagation?: boolean;
};

/**
 * Copy icon button that copies text to the clipboard on click. Does a cool transition to a green checkmark too.
 * Child elements appear to the left of the button.
 */
const CopyArea = ({
  stringToCopy,
  label: rawLabel,
  tooltip,
  tooltipProps = { delayDuration: 300 },
  className,
  iconClassName,
  stopPropagation = false,
}: CopyAreaProps) => {
  const label = rawLabel ?? stringToCopy;

  const [flashTimeout, setFlashTimeout] = useState<number | undefined>(undefined);

  const onClick: MouseEventHandler = ev => {
    if (stopPropagation) {
      ev.stopPropagation();
    }

    setFlashTimeout(prevTimeout => {
      if (prevTimeout) {
        clearTimeout(prevTimeout);
      }

      return window.setTimeout(() => {
        setFlashTimeout(undefined);
      }, 1000);
    });

    copy(stringToCopy);
  };

  // I can't get the icon to switch back to copy when you use tab to focus on
  // it, press enter, then tab away. The icon switch only works when you click.
  const target = (
    <div
      className={twMerge(
        'flex items-center cursor-pointer text-base font-normal pl-0.5 pr-1.5 rounded border border-neutral-300',
        className,
      )}
      onClick={onClick}
    >
      <Icon
        icon={['fas', flashTimeout ? (stringToCopy ? 'check' : 'times') : 'copy']}
        size="sm"
        variant={flashTimeout ? (stringToCopy ? 'success' : 'danger') : 'neutral'}
        iconClassName={iconClassName ?? ''}
      />

      <div>{label}</div>
    </div>
  );

  return tooltip ? <Tooltip text={tooltip} target={target} {...tooltipProps} /> : target;
};

export default CopyArea;
